import { useState } from "react";
import { API } from "../../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Star, Send, Loader2, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const PARAMS = [
  { key: "quality", label: "Quality" },
  { key: "taste", label: "Taste" },
  { key: "packaging", label: "Packaging" },
  { key: "delivery_punctuality", label: "Delivery" },
];

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
          className="transition-transform hover:scale-110 p-0"
        >
          <Star
            size={16}
            className={star <= (hover || value) ? "text-amber-400 fill-amber-400" : "text-gray-300"}
          />
        </button>
      ))}
    </div>
  );
}

export default function ReviewDialog({ open, onClose, order, existingReviews = [] }) {
  const items = order?.items || [];
  const reviewedItemIds = new Set(existingReviews.map((r) => r.item_id));
  const reviewableItems = items.filter((it) => !reviewedItemIds.has(it.item_id));

  const [ratings, setRatings] = useState({});
  const [comments, setComments] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [expandedItem, setExpandedItem] = useState(reviewableItems[0]?.item_id || null);

  if (reviewableItems.length === 0 && open) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-[92vw] sm:max-w-sm rounded-xl">
          <DialogHeader><DialogTitle>All Items Reviewed</DialogTitle></DialogHeader>
          <p className="text-sm text-gray-500 text-center py-4">You've already reviewed all items. Thank you!</p>
          <Button onClick={onClose} className="w-full bg-green-700 hover:bg-green-800 text-white">Done</Button>
        </DialogContent>
      </Dialog>
    );
  }

  const getR = (id) => ratings[id] || { quality: 0, taste: 0, packaging: 0, delivery_punctuality: 0 };
  const setP = (id, p, v) => setRatings((prev) => ({ ...prev, [id]: { ...getR(id), [p]: v } }));
  const done = (id) => Object.values(getR(id)).every((v) => v > 0);
  const overall = (id) => { const v = Object.values(getR(id)).filter((x) => x > 0); return v.length ? (v.reduce((a, b) => a + b, 0) / v.length).toFixed(1) : "0"; };
  const allDone = reviewableItems.every((it) => done(it.item_id));
  const doneCount = reviewableItems.filter((it) => done(it.item_id)).length;

  const submitAll = async () => {
    if (!allDone) {
      toast.error("Please rate all items");
      const inc = reviewableItems.find((it) => !done(it.item_id));
      if (inc) setExpandedItem(inc.item_id);
      return;
    }
    setSubmitting(true);
    let ok = 0;
    for (const item of reviewableItems) {
      try {
        const res = await fetch(`${API}/reviews`, {
          method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
          body: JSON.stringify({ order_id: order.order_id, item_id: item.item_id, ratings: getR(item.item_id), comment: comments[item.item_id] || null }),
        });
        if (res.ok) ok++;
      } catch {}
    }
    setSubmitting(false);
    if (ok > 0) toast.success(ok === reviewableItems.length ? `All ${ok} reviews submitted!` : `${ok}/${reviewableItems.length} reviews submitted`);
    else { toast.error("Failed to submit reviews"); return; }
    onClose(true);
  };

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="max-w-[92vw] sm:max-w-sm rounded-xl p-4 max-h-[85vh] overflow-y-auto"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-1">
          <DialogTitle className="flex items-center gap-2 text-base">
            <Star size={16} className="text-amber-400 fill-amber-400" />
            Rate Your Order
          </DialogTitle>
        </DialogHeader>

        {/* Progress */}
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] text-gray-500 font-medium whitespace-nowrap">{doneCount}/{reviewableItems.length} rated</span>
          <div className="flex-1 h-1 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${(doneCount / reviewableItems.length) * 100}%` }} />
          </div>
        </div>

        {/* Items */}
        <div className="space-y-2">
          {reviewableItems.map((item, idx) => {
            const id = item.item_id;
            const exp = expandedItem === id;
            const c = done(id);
            return (
              <div key={`${id}-${idx}`} className={`border rounded-lg overflow-hidden ${c ? "border-green-200 bg-green-50/40" : "border-gray-200"}`} data-testid={`review-item-${id}`}>
                <button className="w-full flex items-center justify-between px-3 py-2 text-left" onClick={() => setExpandedItem(exp ? null : id)}>
                  <div className="flex-1 min-w-0 mr-2">
                    <p className="font-semibold text-gray-900 text-xs truncate">{item.name}</p>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    {c && <span className="flex items-center gap-0.5 text-[10px] font-bold text-amber-600"><Star size={9} className="fill-amber-400 text-amber-400" />{overall(id)}</span>}
                    {!c && <span className="text-[9px] text-red-400 font-medium">Required</span>}
                    {exp ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                  </div>
                </button>
                {exp && (
                  <div className="px-3 pb-3 border-t border-gray-100 space-y-2 pt-2">
                    {PARAMS.map((p) => (
                      <div key={p.key} className="flex items-center justify-between">
                        <span className="text-xs text-gray-700 w-20">{p.label}</span>
                        <StarRating value={getR(id)[p.key]} onChange={(v) => setP(id, p.key, v)} />
                      </div>
                    ))}
                    <Textarea
                      placeholder="Comment (optional)..."
                      value={comments[id] || ""}
                      onChange={(e) => setComments((prev) => ({ ...prev, [id]: e.target.value }))}
                      className="bg-white text-xs mt-1" rows={2}
                    />
                    {c && idx < reviewableItems.length - 1 && !done(reviewableItems[idx + 1]?.item_id) && (
                      <button className="w-full text-[10px] text-green-600 font-semibold" onClick={() => setExpandedItem(reviewableItems[idx + 1].item_id)}>
                        Next: {reviewableItems[idx + 1].name} &rarr;
                      </button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <Button onClick={submitAll} disabled={!allDone || submitting} className="w-full bg-green-700 hover:bg-green-800 text-white text-sm mt-2 disabled:opacity-40" data-testid="submit-all-reviews-btn">
          {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={12} className="mr-1.5" />}
          Submit All ({doneCount}/{reviewableItems.length})
        </Button>
        <button onClick={() => onClose(false)} className="w-full text-[10px] text-gray-400 hover:text-gray-600 py-0.5">Skip for now</button>
      </DialogContent>
    </Dialog>
  );
}
